/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetProductFeaturesRequest,
  GetProductFeaturesSecurity,
} from "../models/operations/getproductfeatures.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildProductFeaturesQuery,
  prefetchProductFeatures,
  ProductFeaturesQueryData,
  queryKeyProductFeatures,
} from "./productFeatures.core.js";
export {
  buildProductFeaturesQuery,
  prefetchProductFeatures,
  type ProductFeaturesQueryData,
  queryKeyProductFeatures,
};

export type ProductFeaturesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getProductFeatures features
 *
 * @remarks
 * Get the current state of all product feature flags.
 */
export function useProductFeatures(
  request: GetProductFeaturesRequest,
  security?: GetProductFeaturesSecurity | undefined,
  options?: QueryHookOptions<
    ProductFeaturesQueryData,
    ProductFeaturesQueryError
  >,
): UseQueryResult<ProductFeaturesQueryData, ProductFeaturesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildProductFeaturesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getProductFeatures features
 *
 * @remarks
 * Get the current state of all product feature flags.
 */
export function useProductFeaturesSuspense(
  request: GetProductFeaturesRequest,
  security?: GetProductFeaturesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ProductFeaturesQueryData,
    ProductFeaturesQueryError
  >,
): UseSuspenseQueryResult<ProductFeaturesQueryData, ProductFeaturesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildProductFeaturesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setProductFeaturesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: { organizationId: string; gramSession?: string | undefined },
  ],
  data: ProductFeaturesQueryData,
): ProductFeaturesQueryData | undefined {
  const key = queryKeyProductFeatures(...queryKeyBase);

  return client.setQueryData<ProductFeaturesQueryData>(key, data);
}

export function invalidateProductFeatures(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { organizationId: string; gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "features", "get", ...queryKeyBase],
  });
}

export function invalidateAllProductFeatures(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "features", "get"],
  });
}
